import React, { useEffect, useState } from "react";
import { BarChart3, Loader2, Save, Radio } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Card, Btn, Input, Pill } from "./UI";
import { setGA4Id, trackEvent } from "../lib/analytics";

export const AnalyticsSettingsCard = () => {
  const [s, setS] = useState(null);
  const [ga4, setGa4] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/app-settings/analytics").then(({ data }) => { setS(data); setGa4(data.ga4_id || ""); setEnabled(!!data.enabled); }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/app-settings/analytics", { ga4_id: ga4.trim() || null, enabled });
      setS(data); setGa4(data.ga4_id || ""); setEnabled(!!data.enabled);
      setGA4Id(data.enabled ? data.ga4_id : null);
      localStorage.setItem("tr_site_config", JSON.stringify({ ga4_id: data.enabled ? data.ga4_id : null, analytics_enabled: data.enabled }));
      toast.success(data.enabled ? `Google Analytics is live (${data.ga4_id})` : "Google Analytics saved (off)");
    } catch (e) { toast.error(errMsg(e, "Could not save analytics settings")); }
    finally { setBusy(false); }
  };
  const test = () => {
    trackEvent("admin_test_event", { source: "admin_settings" });
    toast.success("Test event sent — check GA4 → Reports → Realtime in ~30s");
  };

  return (
    <Card data-testid="analytics-settings-card">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="text-xs uppercase tracking-wider text-slate-400 font-medium inline-flex items-center gap-2"><BarChart3 className="w-4 h-4 text-amber-300" /> Web analytics · Google Analytics 4</div>
        <span data-testid="analytics-status-pill"><Pill status={s?.enabled ? "Active" : "Paused"}>{s?.enabled ? `Live · ${s.ga4_id}` : "Off"}</Pill></span>
      </div>
      <p className="text-xs text-slate-400 mb-3">Paste your GA4 measurement ID (Google Analytics → Admin → Data streams → Web). Page views and product events (pricing, sign-ups, Find-Your-Race funnel) start flowing the moment you switch it on — no redeploy. The platform-default PostHog snippet has been removed.</p>
      <div className="flex flex-wrap items-end gap-3">
        <Input label="Measurement ID" value={ga4} onChange={(e) => setGa4(e.target.value)} placeholder="G-XXXXXXXXXX" className="w-56 font-mono" data-testid="ga4-id-input" />
        <label className="flex items-center gap-2 text-sm pb-2 cursor-pointer" data-testid="ga4-enabled-toggle">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="accent-amber-400" data-testid="ga4-enabled-checkbox" /> Send data to GA4
        </label>
        <Btn onClick={save} disabled={busy} data-testid="ga4-save-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</Btn>
        {s?.enabled && <Btn variant="secondary" onClick={test} data-testid="ga4-test-btn"><Radio className="w-4 h-4" /> Send test event</Btn>}
      </div>
      {s?.updated_at && <div className="text-[11px] text-slate-500 mt-2" data-testid="analytics-updated">Updated {s.updated_at.slice(0, 16).replace("T", " ")} UTC by {s.updated_by}</div>}
    </Card>
  );
};
